import { useQuery } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  SessionService,
  type ListChargesParams,
} from '~/app/services/session/session.service';
import { SiteService } from '~/app/services/site/site.service';
import { AssetService } from '~/app/services/asset/asset.service';
import { AssetComponentService } from '~/app/services/asset-component/asset-component.service';

export function useSessionCharges(params: ListChargesParams) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [
      'session',
      'charges',
      user?.organizationId,
      params.page,
      params.limit,
      params.q ?? '',
      params.siteName ?? '',
      params.chargerName ?? '',
      params.connectorName ?? '',
      params.sessionStatus ?? '',
      params.anomalyQualification ?? '',
      params.from ?? '',
      params.to ?? '',
    ],
    queryFn: () => SessionService.listCharges(params),
    enabled: Boolean(user?.organizationId),
  });
}

export function useSessionCharge(chargeId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['session', 'charge', user?.organizationId, chargeId],
    queryFn: () => SessionService.getCharge(chargeId),
    enabled: Boolean(user?.organizationId && chargeId),
  });
}

export function useSessionTransitions(
  chargeId: string,
  active: boolean,
  page: number,
  limit: number,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [
      'session',
      'transitions',
      user?.organizationId,
      chargeId,
      page,
      limit,
    ],
    queryFn: () =>
      SessionService.getChargeTransitions(chargeId, { page, limit }),
    enabled: active && Boolean(user?.organizationId && chargeId),
  });
}

export function useSessionProblems(
  chargeId: string,
  active: boolean,
  page: number,
  limit: number,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [
      'session',
      'problems',
      user?.organizationId,
      chargeId,
      page,
      limit,
    ],
    queryFn: () => SessionService.getChargeProblems(chargeId, { page, limit }),
    enabled: active && Boolean(user?.organizationId && chargeId),
  });
}

export function useSessionReadings(
  chargeId: string,
  active: boolean,
  page: number,
  limit: number,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [
      'session',
      'readings',
      user?.organizationId,
      chargeId,
      page,
      limit,
    ],
    queryFn: () => SessionService.getChargeReadings(chargeId, { page, limit }),
    enabled: active && Boolean(user?.organizationId && chargeId),
  });
}

export function useSessionSites(q?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['session', 'sites', user?.organizationId, q ?? ''],
    queryFn: () =>
      SiteService.listByOrganizationId(user!.organizationId!, {
        page: 1,
        limit: 50,
        q: q?.trim() || undefined,
      }),
    enabled: Boolean(user?.organizationId),
  });
}

export function useSessionAssets(q?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['session', 'assets', user?.organizationId, q ?? ''],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId: user!.organizationId!,
        page: 1,
        limit: 50,
        q: q?.trim() || undefined,
      }),
    enabled: Boolean(user?.organizationId),
  });
}

export function useSessionAssetComponents(assetId: string | null, q?: string) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  return useQuery({
    queryKey: ['session', 'asset-components', organizationId, assetId, q ?? ''],
    queryFn: () =>
      AssetComponentService.list(organizationId, assetId!, {
        page: 1,
        limit: 50,
        q: q?.trim() || undefined,
      }),
    enabled: Boolean(organizationId) && Boolean(assetId),
  });
}
